import clsx from 'clsx';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { SVGAttributes } from 'react';

import { ExceptionDimensions, logAnalyticsEvent } from '@vestico/utils';

import { Spinner, SpinnerPositioning, SpinnerSize } from './Spinner';
import style from './svgImage.module.css';

interface IconProps extends SVGAttributes<SVGElement> {
  name: string;
  showSpinner?: boolean;
  onLoadImage?: () => void;
  width?: string;
  height?: string;
}

export const SVGImage: React.FC<IconProps> = ({ name, showSpinner, width, height, onLoadImage, ...rest }) => {
  const importedIconRef = useRef<React.FC<SVGAttributes<SVGElement>>>();
  const [didLoad, setDidLoad] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    setForceUpdate(true);
  }, [name]);

  useEffect(() => {
    setForceUpdate(false);
  }, [forceUpdate]);

  useEffect((): void => {
    setDidLoad(false);

    const importIcon = async (): Promise<void> => {
      try {
        importedIconRef.current = (await import(`sharedimages/${name}.svg`)).default;
        if (onLoadImage) {
          onLoadImage();
        }
      } catch (err) {
        logAnalyticsEvent({
          category: 'exception',
          event: `load-svg-image`,
          name: name,
          dimensions: {
            provider: undefined,
            info: err.message,
          } as ExceptionDimensions,
        });
        console.error(err);
      } finally {
        setDidLoad(true);
      }
    };
    importIcon().catch((err) => {
      console.error(err);
    });
  }, [name, forceUpdate, onLoadImage]);

  if (didLoad && importedIconRef.current) {
    const { current: ImportedIcon } = importedIconRef;
    const cssStyle: CSSProperties = {};
    if (width) {
      cssStyle.width = width;
    }
    if (height) {
      cssStyle.height = height;
    }

    return (
      <div className={clsx(style.wrapper)} style={cssStyle}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <ImportedIcon {...rest} height="100%" />
      </div>
    );
  }

  if (showSpinner) {
    return (
      <div className={clsx(style.spinner)}>
        <Spinner size={SpinnerSize.medium} positioning={SpinnerPositioning.default} />
      </div>
    );
  }

  return null;
};
